import { useEffect, useRef, useState, type FormEvent } from 'react';
import { eventTypes } from '@/mocks/site';

const FORM_URL = 'https://readdy.ai/api/form/db7elo0mkl3spn8sm7u0';
type Status = 'idle' | 'sending' | 'success' | 'error';

const field = 'w-full border-0 border-b border-foreground-950/25 bg-transparent px-0 py-3 text-sm text-foreground-950 placeholder:text-secondary-500 focus:border-primary-500 focus:outline-none focus:ring-0';

export default function InquiryForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');
  const [chars, setChars] = useState(0);
  const topicRef = useRef<HTMLSelectElement>(null);
  const sending = useRef(false);

  useEffect(() => {
    const onTopic = (e: Event) => {
      const topic = (e as CustomEvent<string>).detail;
      if (topicRef.current && eventTypes.includes(topic)) topicRef.current.value = topic;
    };
    window.addEventListener('ff-topic', onTopic);
    return () => window.removeEventListener('ff-topic', onTopic);
  }, []);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (sending.current) return;
    const form = e.currentTarget;
    const data = new FormData(form);
    const trap = String(data.get('website_alt') || '').trim();
    if (trap) { setStatus('success'); return; }
    data.delete('website_alt');

    const name = String(data.get('name') || '').trim();
    const email = String(data.get('email') || '').trim();
    const message = String(data.get('message') || '');
    if (!name || !email) { setStatus('error'); setFormError('Please add your name and email.'); return; }
    if (message.length > 500) { setStatus('error'); setFormError('Please keep your message under 500 characters.'); return; }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setStatus('error'); setFormError('Please enter a valid email address.'); return; }
    sending.current = true;
    setStatus('sending');
    setFormError('');
    try {
      const body = new URLSearchParams();
      data.forEach((v, k) => body.append(k, String(v)));
      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: unknown } } | null = null;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const serverMsg = parsed?.meta?.message || parsed?.message
        || (typeof parsed?.meta?.detail === 'string' ? parsed.meta.detail : '') || responseText;
      if (response.ok && parsed?.code === 'OK' && !/spam/i.test(serverMsg || '')) {
        setStatus('success');
        form.reset();
        setChars(0);
      } else {
        setStatus('error');
        setFormError(serverMsg || 'Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setFormError('Network error. Please check your connection and try again.');
    } finally {
      sending.current = false;
    }
  };

  return (
    <form id="project-inquiry" data-readdy-form onSubmit={onSubmit} className="relative grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6" noValidate>
      <label className="block">
        <span className="font-label text-[11px] uppercase tracking-[0.16em] text-secondary-600">Name *</span>
        <input name="name" type="text" required autoComplete="name" placeholder="Your full name" className={field} />
      </label>
      <label className="block">
        <span className="font-label text-[11px] uppercase tracking-[0.16em] text-secondary-600">Email *</span>
        <input name="email" type="email" required autoComplete="email" placeholder="you@company.com" className={field} />
      </label>
      <label className="block">
        <span className="font-label text-[11px] uppercase tracking-[0.16em] text-secondary-600">Company / organisation</span>
        <input name="company" type="text" autoComplete="organization" placeholder="Optional" className={field} />
      </label>
      <label className="block">
        <span className="font-label text-[11px] uppercase tracking-[0.16em] text-secondary-600">Topic</span>
        <select ref={topicRef} name="event_type" defaultValue={eventTypes[0]} className={`${field} cursor-pointer`}>
          {eventTypes.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </label>
      <label className="block sm:col-span-2">
        <span className="font-label text-[11px] uppercase tracking-[0.16em] text-secondary-600">Tell us about the idea</span>
        <textarea name="message" rows={3} maxLength={500} onChange={(e) => setChars(e.target.value.length)} placeholder="Timing, place, audience, ambition. Whatever you have so far." className={`${field} resize-none`} />
        <span className="mt-1 block text-right font-label text-[11px] text-secondary-500">{chars}/500</span>
      </label>
      <input type="text" name="website_alt" className="ff-alt-field" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly onFocus={(e) => e.currentTarget.removeAttribute('readonly')} />

      <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
        <button type="submit" disabled={status === 'sending'} className="inline-flex items-center justify-center gap-2 rounded-full bg-foreground-950 px-7 py-4 text-sm font-semibold text-background-50 hover:bg-primary-500 disabled:opacity-60 transition-colors whitespace-nowrap cursor-pointer">
          {status === 'sending' ? 'Sending...' : 'Send inquiry'}
          <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line" /></span>
        </button>
        <div aria-live="polite" className="text-sm">
          {status === 'success' && <p className="text-primary-700 font-medium">Thank you. Your message is with us and we&apos;ll reply soon.</p>}
          {status === 'error' && <p className="text-foreground-950 font-medium">{formError}</p>}
        </div>
      </div>
    </form>
  );
}